<template>
  <div class="pass-history-view v3-pass-history behaviour-settings-core-value">
    <h2 class="sr-only">
      {{ behaviourTerminology.coreValueSectionTitle }}
    </h2>
    <output class="sr-only" aria-live="polite" aria-atomic="true">{{
      liveAnnouncement
    }}</output>
    <Loader :is-processing="state.isLoading" />
    <ActionPanel class="mb-3 behaviour-core-value-action-panel">
      <template #left-content>
        <div
          class="d-flex flex-wrap align-items-end gap-3 flex-grow-1 min-w-0 behaviour-core-value-toolbar"
        >
          <div class="behaviour-core-value-search-field min-w-0">
            <label
              id="behaviour-core-value-search-label"
              class="v3-form-label d-block mb-1"
              for="behaviour-core-value-search"
            >
              {{ $t("common.search") }}
            </label>
            <InputField
              class="behaviour-core-value-search"
              prepend-icon="ri-search-line"
              :model-value="state.searchInput"
              :placeholder="$t('common.search')"
              input-type="search"
              id="behaviour-core-value-search"
              data-testid="behaviour-core-value-search"
              @update:model-value="onSearchFieldInput"
            />
          </div>
          <div class="behaviour-core-value-status-field min-w-0">
            <label
              id="behaviour-core-value-status-filter-label"
              class="v3-form-label d-block mb-1"
              for="behaviour-core-value-status-filter-input"
            >
              {{ $t("behaviour_settings.status_filter_label") }}
            </label>
            <CustomSelect
              v-model="state.statusFilterValue"
              class="behaviour-core-value-status-filter"
              hide-floating-label
              labelled-by="behaviour-core-value-status-filter-label"
              :options="statusFilterOptions"
              :close-on-select="true"
              :searchable="false"
              :reduce="(item) => item.value"
              input-id="behaviour-core-value-status-filter-input"
              data-testid="behaviour-core-value-status-filter"
              @update:model-value="onStatusFilterChange"
            />
          </div>
        </div>
      </template>
      <template #right-content>
        <div
          class="d-flex align-items-center flex-wrap justify-content-end gap-2"
        >
          <button
            type="button"
            class="btn btn-link d-flex align-items-center p-0 text-primary behaviour-core-value-reset text-decoration-none"
            :disabled="state.isLoading"
            data-testid="behaviour-core-value-reset-filters"
            @click="resetFilters"
          >
            <i class="ri-filter-off-line" aria-hidden="true" />
            <span class="ms-1 text-decoration-underline">{{
              $t("behaviour_settings.reset_filters")
            }}</span>
          </button>
          <BaseButton
            rounded
            data-testid="behaviour-core-value-upload-csv"
            type="button"
            prepend-icon="ri-upload-2-line"
            :is-disabled="state.isLoading"
            @click="onUploadCsv"
          >
            {{ $t("behaviour_settings.upload_csv") }}
          </BaseButton>
          <BaseButton
            solid
            rounded
            data-testid="behaviour-core-value-add"
            type="button"
            @click="onAddCoreValue"
          >
            {{ behaviourTerminology.addCoreValue }}
          </BaseButton>
        </div>
      </template>
    </ActionPanel>

    <PerPage
      v-if="state.pagination && state.pagination.total > 0"
      :pagination="state.pagination"
      :showing-entries="true"
      @on-page-change="setPerPage"
    />

    <DataTable
      class="datatable bg-white text-center mt-3"
      add-table-classes="behaviour-core-value-datatable"
      :fields="tableFields"
      :items="state.tableItems"
      :sorter="{ external: true, resetable: false }"
      :sorter-value="sorterValue"
      :no-items-view="noItemsView"
      data-testid="behaviour-core-value-table"
      @update:sorter-value="onSortColumnChange"
    >
      <template #indicator="{ item }">
        <td class="text-start align-middle">
          <span
            class="behaviour-core-value-dot"
            :class="indicatorDotClass(item)"
            :style="indicatorDotStyle(item)"
            aria-hidden="true"
          />
          <span class="visually-hidden">{{ indicatorAriaLabel(item) }}</span>
        </td>
      </template>
      <template #status="{ item }">
        <td class="text-start">{{ formatStatusLabel(item) }}</td>
      </template>
      <template #actions="{ item }">
        <td class="text-end">
          <Actions
            :actions="rowActionsForItem(item)"
            :row-context-label="rowContextLabel(item)"
            @click-action="handleRowAction($event, item)"
          />
        </td>
      </template>
    </DataTable>

    <div
      v-if="state.isLazyLoadingMode && canLoadMoreLazy"
      ref="lazySentinelRef"
      class="behaviour-core-value-lazy-sentinel py-2 text-center text-muted small"
      data-testid="behaviour-core-value-lazy-sentinel"
      aria-hidden="true"
    >
      {{ $t("behaviour_settings.lazy_scroll_hint") }}
    </div>

    <Pagination
      v-if="
        state.pagination.pages &&
        state.pagination.pages > 1 &&
        !state.isLazyLoadingMode
      "
      :active-page="state.pagination.activePage"
      :pages="state.pagination.pages"
      class="cs-pagination mt-4"
      :dots="false"
      :double-arrows="true"
      size="lg"
      align="center"
      @update:active-page="setActivePage"
    />
  </div>
</template>

<script>
import { buildPaginationFromApiResponse } from "@/helpers/behaviourCoreValueApi.js"
import {
  indicatorAriaColorFor,
  indicatorDotClassFor,
  indicatorDotStyleFor
} from "@/helpers/behaviourCoreValueIndicator.js"
import BehaviourCreateCoreValueForm from "@/v3components/Forms/Behaviour/BehaviourCreateCoreValueForm.vue"
import BehaviourCsvUploadForm from "@/v3components/Forms/Behaviour/BehaviourCsvUploadForm.vue"
import BehaviourDeleteCoreValueForm from "@/v3components/Forms/Behaviour/BehaviourDeleteCoreValueForm.vue"
import behaviourConstants from "@/constants/behaviourManagementConstants.js"
import behaviourCoreValueConstants from "@/constants/behaviourCoreValueConstants.js"
import behaviourCsvUploadConstants from "@/constants/behaviourCsvUploadConstants.js"
import { resolveBehaviourCsvUploadModalTitle } from "@/helpers/behaviourCsvUpload.js"
import ActionPanel from "@/v3components/shared/DataTable/ActionPanel.vue"
import Actions from "@/v3components/shared/DataTable/Actions.vue"
import BaseButton from "@/v3components/shared/Buttons/BaseButton.vue"
import CustomSelect from "@/v3components/shared/Form/CustomSelect.vue"
import DataTable from "@/v3components/shared/DataTable/DataTable.vue"
import InputField from "@/v3components/shared/Form/InputField.vue"
import Loader from "@/v3components/shared/Loader/Loader.vue"
import Pagination from "@/v3components/shared/DataTable/Pagination.vue"
import PerPage from "@/v3components/shared/DataTable/Perpage.vue"
import {
  computed,
  inject,
  nextTick,
  onMounted,
  onUnmounted,
  reactive,
  ref,
  watch
} from "vue"
import { formatBehaviourTerminologyTemplate } from "@/configs/behaviourTerminology.js"
import { useI18n } from "vue-i18n"
import { useStore } from "vuex"

const SEARCH_DEBOUNCE_MS = 320

export default {
  name: "BehaviourSettingsCoreValueTab",
  components: {
    ActionPanel,
    Actions,
    BaseButton,
    CustomSelect,
    DataTable,
    InputField,
    Loader,
    Pagination,
    PerPage
  },
  setup() {
    const { t } = useI18n()
    const store = useStore()
    const modal = inject("modal")
    const lazySentinelRef = ref(null)
    const liveAnnouncement = ref("")
    const statusUpdateId = ref(null)
    let searchDebounceId = null
    let lazyObserver = null

    const behaviourTerminology = computed(
      () => store.getters["behaviourSettings/behaviourTerminology"]
    )

    const noItemsView = computed(() => {
      const bt = behaviourTerminology.value
      return {
        noResults: bt.noRows,
        noItems: bt.noRows
      }
    })

    const statusFilterOptions = computed(() => [
      { label: t("behaviour_settings.status_all"), value: "all" },
      {
        label: behaviourCoreValueConstants.STATUS.ENABLED,
        value: "active"
      },
      {
        label: behaviourCoreValueConstants.STATUS.DISABLED,
        value: "inactive"
      }
    ])

    const state = reactive({
      isLoading: false,
      searchInput: "",
      statusFilterValue: "all",
      /** Server-side sort: one of `name`/`description`/`status` or null. */
      sortBy: null,
      /** Server-side sort order: `asc`/`desc` or null. */
      sortOrder: null,
      isLazyAppendInFlight: false,
      pagination: {
        activePage: 1,
        total: 0,
        pages: 0,
        from: 0,
        to: 0,
        per_page: { label: "25", value: 25 }
      },
      isLazyLoadingMode: false,
      lazyLoadedCount: 0,
      tableItems: []
    })

    /** Sorter value bound to the DataTable; mirrors the active sort state. */
    const sorterValue = computed(() => ({
      column: state.sortBy || "",
      asc: state.sortOrder !== "desc"
    }))

    const rowActionsForItem = (item) => {
      const actions = [
        {
          label: t("behaviour_settings.action_edit"),
          icon: "ri-pencil-line",
          action: "edit"
        },
        {
          label: t("behaviour_settings.action_delete"),
          icon: "ri-delete-bin-line",
          class: "important",
          action: "delete"
        }
      ]
      if (item?.status === "active") {
        actions.push({
          label: t("behaviour_settings.action_disable"),
          icon: "ri-forbid-line",
          action: "disable"
        })
      } else if (item?.status === "inactive") {
        actions.push({
          label: t("behaviour_settings.action_enable"),
          icon: "ri-checkbox-circle-line",
          action: "enable"
        })
      }
      return actions
    }

    const rowContextLabel = (item) => {
      const bt = store.getters["behaviourSettings/behaviourTerminology"]
      const name = item?.name
      if (name) return String(name)
      if (item?.id != null) return `${bt.coreValue} ${item.id}`
      return bt.coreValue
    }

    const tableFields = computed(() => [
      {
        key: "indicator",
        label: "",
        sorter: false,
        filter: false,
        _style: "width:48px"
      },
      {
        key: "name",
        label: t("behaviour_settings.col_name"),
        sorter: true,
        filter: false
      },
      {
        key: "description",
        label: t("behaviour_settings.col_description"),
        sorter: true,
        filter: false
      },
      {
        key: "status",
        label: t("behaviour_settings.col_status"),
        sorter: true,
        filter: false
      },
      {
        key: "actions",
        label: t("behaviour_settings.col_actions"),
        sorter: false,
        filter: false,
        _style: "width: 96px;"
      }
    ])

    const formatStatusLabel = (item) => {
      if (item?.status === "active") {
        return behaviourCoreValueConstants.STATUS.ENABLED
      }
      if (item?.status === "inactive") {
        return behaviourCoreValueConstants.STATUS.DISABLED
      }
      return ""
    }

    const announce = (message) => {
      liveAnnouncement.value = ""
      if (!message) return
      requestAnimationFrame(() => {
        liveAnnouncement.value = message
      })
    }

    const fetchCoreValuesPage = async ({ page, append = false } = {}) => {
      const perPageVal = Number(state.pagination.per_page?.value) || 25
      const perPageLabel =
        state.pagination.per_page?.label || String(perPageVal)
      const isLazyMode =
        perPageVal === behaviourConstants.CORE_VALUE_TABLE_ALL_ENTRIES_PER_PAGE
      const lazyChunkSize =
        behaviourConstants.CORE_VALUE_TABLE_ALL_ENTRIES_PER_PAGE
      const requestPerPage = isLazyMode ? lazyChunkSize : perPageVal
      const requestPage = page ?? state.pagination.activePage ?? 1

      const { rows, pagination } = await store.dispatch(
        "behaviourSettings/fetchCoreValuesList",
        {
          page: requestPage,
          perPage: requestPerPage,
          search: state.searchInput,
          statusFilter: state.statusFilterValue || "all",
          sortBy: state.sortBy,
          sortOrder: state.sortOrder
        }
      )

      state.isLazyLoadingMode = isLazyMode
      state.pagination = buildPaginationFromApiResponse(pagination, {
        perPageLabel,
        perPageValue: isLazyMode ? lazyChunkSize : perPageVal
      })
      state.pagination.per_page = { label: perPageLabel, value: perPageVal }

      if (isLazyMode && append) {
        state.tableItems = [...state.tableItems, ...rows]
        state.lazyLoadedCount = state.tableItems.length
      } else {
        state.tableItems = rows
        state.lazyLoadedCount = rows.length
      }

      return { rows, isLazyMode }
    }

    const refreshTable = async ({ append = false } = {}) => {
      try {
        if (append) {
          state.isLazyAppendInFlight = true
        } else {
          state.isLoading = true
        }

        const requestPage = append
          ? (Number(state.pagination.activePage) || 1) + 1
          : Number(state.pagination.activePage) || 1

        const { isLazyMode } = await fetchCoreValuesPage({
          page: requestPage,
          append
        })

        if (append && isLazyMode) {
          state.pagination.activePage = requestPage
        }

        const total = state.pagination.total || 0
        const count = state.tableItems.length

        if (isLazyMode) {
          announce(
            formatBehaviourTerminologyTemplate(
              behaviourTerminology.value.announceLoadedLazy,
              { count, total }
            )
          )
        } else {
          announce(
            formatBehaviourTerminologyTemplate(
              behaviourTerminology.value.announceLoadedPaginated,
              { count, total }
            )
          )
        }
      } catch {
        if (!append) {
          state.tableItems = []
          state.pagination = {
            ...state.pagination,
            total: 0,
            pages: 0,
            from: 0,
            to: 0
          }
        }
        announce(
          t("behaviour_settings.core_value_list_load_error", {
            coreValuesLower: behaviourTerminology.value.coreValuesLower
          })
        )
      } finally {
        state.isLoading = false
        state.isLazyAppendInFlight = false
        await nextTick()
        setupLazyObserver()
      }
    }

    const canLoadMoreLazy = computed(() => {
      if (!state.isLazyLoadingMode) return false
      const currentPage = Number(state.pagination.activePage) || 1
      const totalPages = Number(state.pagination.pages) || 1
      return currentPage < totalPages
    })

    const appendLazyChunk = async () => {
      if (
        !state.isLazyLoadingMode ||
        state.isLoading ||
        state.isLazyAppendInFlight ||
        !canLoadMoreLazy.value
      ) {
        return
      }

      const previousVisible = state.tableItems.length
      await refreshTable({ append: true })

      announce(
        formatBehaviourTerminologyTemplate(
          behaviourTerminology.value.announceLazyMore,
          {
            visible: state.tableItems.length,
            total: state.pagination.total || 0
          }
        )
      )

      if (state.tableItems.length === previousVisible) {
        teardownLazyObserver()
      }
    }

    const teardownLazyObserver = () => {
      if (lazyObserver) {
        lazyObserver.disconnect()
        lazyObserver = null
      }
    }

    const setupLazyObserver = () => {
      teardownLazyObserver()
      if (!state.isLazyLoadingMode || !canLoadMoreLazy.value) return
      const el = lazySentinelRef.value
      if (!el || !("IntersectionObserver" in window)) return
      lazyObserver = new IntersectionObserver(
        (entries) => {
          const [entry] = entries
          if (entry?.isIntersecting) {
            appendLazyChunk()
          }
        },
        { root: null, rootMargin: "80px", threshold: 0.15 }
      )
      lazyObserver.observe(el)
    }

    watch(
      () => [state.isLazyLoadingMode, canLoadMoreLazy.value],
      () => {
        nextTick(() => setupLazyObserver())
      }
    )

    const onSearchFieldInput = (value) => {
      state.searchInput = value || ""
      if (searchDebounceId) {
        clearTimeout(searchDebounceId)
      }
      searchDebounceId = setTimeout(() => {
        state.pagination.activePage = 1
        refreshTable()
      }, SEARCH_DEBOUNCE_MS)
    }

    const resetFilters = () => {
      if (state.isLoading) return
      state.searchInput = ""
      state.statusFilterValue = "all"
      state.sortBy = null
      state.sortOrder = null
      state.pagination.activePage = 1
      if (searchDebounceId) {
        clearTimeout(searchDebounceId)
        searchDebounceId = null
      }
      refreshTable()
    }

    const onStatusFilterChange = () => {
      state.pagination.activePage = 1
      refreshTable()
    }

    /**
     * Called by `<DataTable>` when the user clicks a sortable column header.
     * With `resetable: false`, the same column toggles asc → desc → asc.
     */
    const onSortColumnChange = (sorterState) => {
      const column = String(sorterState?.column ?? "").trim()
      if (!column) {
        state.sortBy = null
        state.sortOrder = null
      } else {
        state.sortBy = column
        state.sortOrder = sorterState?.asc ? "asc" : "desc"
      }
      state.pagination.activePage = 1
      refreshTable()
    }

    const setPerPage = (option) => {
      if (!option) return
      state.pagination.per_page = option
      state.pagination.activePage = 1
      refreshTable()
    }

    const setActivePage = (page) => {
      state.pagination.activePage = page
      refreshTable()
    }

    const indicatorDotClass = indicatorDotClassFor
    const indicatorDotStyle = indicatorDotStyleFor
    const indicatorAriaLabel = (item) =>
      t("behaviour_settings.indicator_a11y", {
        color: indicatorAriaColorFor(item)
      })

    const setCoreValueStatus = async (item, nextStatus) => {
      if (!item?.id || !nextStatus || statusUpdateId.value != null) {
        return false
      }
      if (item.status === nextStatus) {
        return false
      }

      try {
        statusUpdateId.value = item.id
        await store.dispatch("behaviourSettings/updateCoreValueStatus", {
          id: item.id,
          existingRow: item,
          status: nextStatus
        })
        const index = state.tableItems.findIndex((row) => row?.id === item.id)
        if (index >= 0) {
          state.tableItems[index] = {
            ...state.tableItems[index],
            status: nextStatus
          }
        }
        return true
      } catch {
        announce(
          t("behaviour_settings.core_value_updated_error", {
            coreValue: behaviourTerminology.value.coreValue
          })
        )
        return false
      } finally {
        statusUpdateId.value = null
      }
    }

    const getExistingNamesExcluding = (excludeId) =>
      state.tableItems
        .filter((row) => row?.id !== excludeId)
        .map((row) => row?.name)
        .filter(Boolean)

    const openEditCoreValue = (item) => {
      if (!item?.id) return
      const bt = behaviourTerminology.value
      modal?.open?.(BehaviourCreateCoreValueForm, {
        size: "lg",
        title: t("behaviour_settings.edit_core_value_title", {
          coreValue: bt.coreValue
        }),
        props: {
          editableCoreValue: { ...item },
          existingNames: getExistingNamesExcluding(item.id)
        }
      })
    }

    const openDeleteCoreValue = async (item) => {
      if (!item?.id) return
      const bt = behaviourTerminology.value
      let transferTargets = state.tableItems.filter(
        (row) => row?.id !== item.id
      )
      try {
        const { rows } = await store.dispatch(
          "behaviourSettings/fetchCoreValuesList",
          {
            page: 1,
            perPage: behaviourConstants.CORE_VALUE_TABLE_ALL_ENTRIES_PER_PAGE,
            search: "",
            statusFilter: "all"
          }
        )
        transferTargets = rows.filter((row) => row?.id !== item.id)
      } catch {
        /* Fall back to rows already loaded in the table */
      }
      modal?.open?.(BehaviourDeleteCoreValueForm, {
        size: "lg",
        title: t("behaviour_settings.delete_core_value_title", {
          coreValue: bt.coreValue
        }),
        props: {
          coreValue: { ...item },
          transferTargets
        }
      })
    }

    const handleRowAction = async (action, item) => {
      const key = action?.action
      if (!key || !item) return
      switch (key) {
        case "disable":
          if (item?.status === "active") {
            const disabled = await setCoreValueStatus(item, "inactive")
            if (disabled) {
              announce(
                t("behaviour_settings.core_value_disabled_success", {
                  coreValue: behaviourTerminology.value.coreValue
                })
              )
            }
          }
          break
        case "enable":
          if (item?.status === "inactive") {
            const enabled = await setCoreValueStatus(item, "active")
            if (enabled) {
              announce(
                t("behaviour_settings.core_value_enabled_success", {
                  coreValue: behaviourTerminology.value.coreValue
                })
              )
            }
          }
          break
        case "edit":
          openEditCoreValue(item)
          break
        case "delete":
          openDeleteCoreValue(item)
          break
        default:
          break
      }
    }

    const onAddCoreValue = () => {
      const bt = behaviourTerminology.value
      modal?.open?.(BehaviourCreateCoreValueForm, {
        size: "lg",
        title: bt.addCoreValue,
        props: {
          existingNames: []
        }
      })
    }

    const clearModalAction = () => {
      if (!modal?.options) return
      modal.options.action = ""
      modal.options.actionData = null
    }

    watch(
      () => modal?.options?.action,
      (action) => {
        const actionData = modal?.options?.actionData
        if (!action || !actionData) return

        const coreValueTerm = behaviourTerminology.value.coreValue

        if (action === "coreValueCreated") {
          state.pagination.activePage = 1
          clearModalAction()
          refreshTable()
          announce(
            t("behaviour_settings.core_value_created_success", {
              coreValue: coreValueTerm
            })
          )
          return
        }

        if (action === "coreValueUpdated") {
          clearModalAction()
          refreshTable()
          announce(
            t("behaviour_settings.core_value_updated_success", {
              coreValue: coreValueTerm
            })
          )
          return
        }

        if (action === "coreValueDeleted") {
          clearModalAction()
          refreshTable()
          announce(
            t("behaviour_settings.core_value_deleted_success", {
              coreValue: coreValueTerm
            })
          )
          return
        }

        if (action === "coreValuesCsvUploaded") {
          state.pagination.activePage = 1
          clearModalAction()
          refreshTable()
          const inserted = Number(actionData?.inserted) || 0
          const failed = Number(actionData?.failed) || 0
          if (failed > 0) {
            announce(
              t("behaviour_settings.upload_csv_partial_success", {
                inserted,
                failed,
                total: Number(actionData?.totalRows) || inserted + failed
              })
            )
          } else {
            announce(
              t("behaviour_settings.upload_csv_success", {
                inserted,
                total: Number(actionData?.totalRows) || inserted
              })
            )
          }
        }
      }
    )

    const onUploadCsv = () => {
      const bt = behaviourTerminology.value
      modal?.open?.(BehaviourCsvUploadForm, {
        size: "sm",
        title: resolveBehaviourCsvUploadModalTitle(
          behaviourCsvUploadConstants.UPLOAD_KIND.CORE_VALUE,
          bt,
          t
        ),
        props: {
          uploadKind: behaviourCsvUploadConstants.UPLOAD_KIND.CORE_VALUE
        }
      })
    }

    onMounted(() => {
      store.dispatch("behaviourSettings/fetchBehaviourTerminology")
      refreshTable()
    })

    onUnmounted(() => {
      teardownLazyObserver()
      if (searchDebounceId) {
        clearTimeout(searchDebounceId)
      }
    })

    return {
      state,
      behaviourTerminology,
      noItemsView,
      tableFields,
      rowActionsForItem,
      rowContextLabel,
      statusFilterOptions,
      liveAnnouncement,
      lazySentinelRef,
      canLoadMoreLazy,
      onSearchFieldInput,
      resetFilters,
      onStatusFilterChange,
      onSortColumnChange,
      sorterValue,
      setPerPage,
      setActivePage,
      formatStatusLabel,
      indicatorDotClass,
      indicatorDotStyle,
      indicatorAriaLabel,
      handleRowAction,
      onAddCoreValue,
      onUploadCsv
    }
  }
}
</script>
